import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { DropResult } from "react-beautiful-dnd";

import { showErrorToast } from "@components/Common/ErrorToast";
import { logger } from "@helpers/logger";
import { RootState } from "../../../../../../redux/store";
import { setPreviewLanguage } from "../../../../../../redux/scanToSlice";
import { ScanToContext } from "..";

import { PropsType, ImageItem } from "./types";
import {
    DEFAULT_TRANSLATIONS,
    LANGUAGE_CODES,
    ACTIVITY_BANNER_IMAGES,
} from "./constants";
import {
    DISPLAY_TYPE_MOBILE,
    DISPLAY_KEY_MOBILE,
    DISPLAY_KEY_DESKTOP,
    SCAN_TO_DEFAULT_MAIN_COLOR,
    ICON_SIZE_SMALL,
} from "../shared/constants";
import {
    Container,
    LeftSection,
    SectionHeader,
    BannerHeaderGroup,
    SectionTitle,
    ToggleContainer,
    ToggleLabel,
    Toggle,
    ResetAllButton,
} from "./styled";
import { ImageTable } from "./ImageTable";
import { TitleDescription } from "./TitleDescription";
import { DeleteConfirmModal } from "./DeleteConfirmModal";
import { ConfirmationInitializationMessage } from "@components/Common/ConfirmationInitializationMessage";
import { DEFAULT_BANNER_IMAGES } from "../../../../../../redux/scanToSlice";
import { ResetIcon } from "@components/Common/SvgIcons/ResetIcon";

export function Banniere({ rowData }: PropsType): JSX.Element {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const mainColor =
        useSelector((state: RootState) => state.global.infoUser.maincolor) ||
        SCAN_TO_DEFAULT_MAIN_COLOR;
    // Shared language the whole preview renders in. Any rubric's picker
    // updates it, so the preview stays consistent across sections.
    const previewLanguage = useSelector(
        (state: RootState) => state.scanTo.previewLanguage
    );

    const {
        selectedActivity,
        setSharedBannerData,
        registerPendingFile,
        removePendingFile,
        registerDeletedImage,
    } = React.useContext(ScanToContext);

    // Cast: the slice's BannerConfig type doesn't declare every UI-state field
    // that lives at runtime (selectedLanguage, etc.). Out of scope for ARCH-02.
    const savedBanner = useSelector(
        (state: RootState) => state.scanTo?.banner
    ) as any;
    const [bannerEnabled, setBannerEnabled] = React.useState(
        savedBanner?.bannerEnabled ?? true
    );
    // The editing language IS the shared preview language: editing and the
    // preview always show the same locale, and picking it here flips every
    // other section too. Aliased so existing selectedLanguage /
    // setSelectedLanguage call sites keep working unchanged.
    const selectedLanguage = previewLanguage;
    const setSelectedLanguage = React.useCallback(
        (lang: string) => dispatch(setPreviewLanguage(lang)),
        [dispatch]
    );
    const [currentSlide, setCurrentSlide] = React.useState(0);
    const [displayType, setDisplayType] = React.useState(
        savedBanner?.displayType || "desktop"
    );
    const [images, setImages] = React.useState<{
        Mobile: ImageItem[];
        Desktop: ImageItem[];
    }>(() => {
        const raw = savedBanner?.images;
        // Handle old flat-array format → migrate to new { Mobile, Desktop } structure
        if (Array.isArray(raw)) return { Mobile: [], Desktop: raw };
        if (raw && raw.Mobile !== undefined) return raw;
        return { Mobile: [], Desktop: [] };
    });
    // Computed: current images list based on displayType
    const displayKey: keyof typeof images =
        displayType === DISPLAY_TYPE_MOBILE
            ? DISPLAY_KEY_MOBILE
            : DISPLAY_KEY_DESKTOP;
    const currentImages = images[displayKey];
    const setCurrentImages = (updated: ImageItem[]) => {
        setImages({ ...images, [displayKey]: updated });
    };

    const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
    const [imageToDelete, setImageToDelete] = React.useState<number | null>(
        null
    );
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [uploadedImages, setUploadedImages] = React.useState<
        Record<number, string>
    >({});

    // Visibility states
    const [titleVisible, setTitleVisible] = React.useState(
        savedBanner?.titleVisible ?? true
    );
    const [descriptionVisible, setDescriptionVisible] = React.useState(
        savedBanner?.descriptionVisible ?? true
    );
    // Confirmation dialog for the whole-banner reset.
    const [isBannerResetOpen, setIsBannerResetOpen] = React.useState(false);

    // Color states for title and description
    const [titleColor, setTitleColor] = React.useState(
        savedBanner?.titleColor || "#ffffff"
    );
    const [descriptionColor, setDescriptionColor] = React.useState(
        savedBanner?.descriptionColor || "#ffffff"
    );

    // Default translations for each language
    const defaultTranslations = DEFAULT_TRANSLATIONS;

    // Custom texts per language
    const [customTexts, setCustomTexts] = React.useState<
        Record<string, { title: string; description: string }>
    >(
        savedBanner?.customTexts || {
            fr: {
                title: defaultTranslations.fr.title,
                description: defaultTranslations.fr.description,
            },
            en: {
                title: defaultTranslations.en.title,
                description: defaultTranslations.en.description,
            },
            ru: {
                title: defaultTranslations.ru.title,
                description: defaultTranslations.ru.description,
            },
            it: {
                title: defaultTranslations.it.title,
                description: defaultTranslations.it.description,
            },
            es: {
                title: defaultTranslations.es.title,
                description: defaultTranslations.es.description,
            },
            pt: {
                title: defaultTranslations.pt.title,
                description: defaultTranslations.pt.description,
            },
            de: {
                title: defaultTranslations.de.title,
                description: defaultTranslations.de.description,
            },
            ar: {
                title: defaultTranslations.ar.title,
                description: defaultTranslations.ar.description,
            },
        }
    );

    const languages = React.useMemo(
        () =>
            LANGUAGE_CODES.map((l) => ({ code: l.code, label: t(l.labelKey) })),
        [t]
    );

    // Banner images for each activity (2 images per activity) — local assets
    const activityBannerImages = ACTIVITY_BANNER_IMAGES;

    // One AbortController for the whole mount lifecycle: every default-image
    // fetch (on-mount seed AND reset re-seeds) shares its signal, and the
    // on-mount effect's cleanup abort()s it at unmount. This cancels in-flight
    // fetches so their `.then` never runs post-unmount — preventing both the
    // orphan blob URL (createObjectURL after the unmount revoke cleanup ran →
    // never revoked) and the setUploadedImages-on-unmounted-component warning.
    const abortControllerRef = React.useRef<AbortController | null>(null);
    if (abortControllerRef.current === null) {
        abortControllerRef.current = new AbortController();
    }

    // Fetch the local default asset for each row that lacks a real URL and
    // stash the resulting blob URL in `uploadedImages`, so the table shows the
    // actual image instead of the placeholder. Shared by the on-mount seed and
    // the reset handlers — a reset clears `uploadedImages`, so it must re-run
    // (otherwise restored defaults render as placeholders until reload).
    const loadDefaultImageBlobs = React.useCallback(
        (list: ImageItem[]) => {
            const signal = abortControllerRef.current?.signal;
            list.forEach((img) => {
                if (img.url || img.filename?.startsWith("http")) return;

                const activityDefaults =
                    activityBannerImages[img.activityId] || [];
                const actIdx = list
                    .filter((i) => i.activityId === img.activityId)
                    .findIndex((i) => i.id === img.id);
                const defaultSrc =
                    activityDefaults[actIdx] || activityDefaults[0];
                if (!defaultSrc) return;

                fetch(defaultSrc, { signal })
                    .then((res) => res.blob())
                    .then((blob) => {
                        // Bail if the component unmounted while the fetch was
                        // in flight: don't create an orphan blob URL and don't
                        // setState on an unmounted component.
                        if (signal?.aborted) return;
                        const file = new File(
                            [blob],
                            img.filename ||
                                `banner_${img.activityId}_${actIdx + 1}.jpg`,
                            { type: blob.type }
                        );
                        const blobUrl = URL.createObjectURL(blob);
                        registerPendingFile(blobUrl, file);
                        setUploadedImages((prev) => ({
                            ...prev,
                            [img.id]: blobUrl,
                        }));
                    })
                    .catch((err) => {
                        // Aborts are expected when the rubric unmounts
                        // mid-flight (see the on-mount effect cleanup) — not a
                        // real failure, so stay quiet. Any other error (missing
                        // asset, CORS, decode) leaves an empty thumbnail, so
                        // trace it via the app logger to make it observable
                        // instead of swallowing it. Non-blocking either way.
                        if (
                            signal?.aborted ||
                            (err instanceof Error && err.name === "AbortError")
                        ) {
                            return;
                        }
                        logger.error(
                            `[ScanTo Banner] default image fetch failed for "${defaultSrc}"`,
                            err
                        );
                    });
            });
        },
        [activityBannerImages, registerPendingFile]
    );

    // Handle drag and drop
    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) return;

        const sourceIndex = result.source.index;
        const destinationIndex = result.destination.index;

        if (sourceIndex === destinationIndex) return;

        const reorderedImages = Array.from(currentImages);
        const [movedImage] = reorderedImages.splice(sourceIndex, 1);
        reorderedImages.splice(destinationIndex, 0, movedImage);

        // Update order values
        const updatedImages = reorderedImages.map((img, index) => ({
            ...img,
            order: index + 1,
        }));

        setCurrentImages(updatedImages);
    };

    // Handle manual order change
    const handleOrderChange = (imageId: number, newOrder: string) => {
        const orderNum = parseInt(newOrder, 10);

        if (isNaN(orderNum) || orderNum < 1 || orderNum > currentImages.length)
            return;

        const currentImage = currentImages.find((img) => img.id === imageId);
        if (!currentImage) return;

        const currentIndex = currentImages.findIndex(
            (img) => img.id === imageId
        );
        const newIndex = orderNum - 1;

        if (currentIndex === newIndex) return;

        const reorderedImages = Array.from(currentImages);
        reorderedImages.splice(currentIndex, 1);
        reorderedImages.splice(newIndex, 0, currentImage);

        // Update order values
        const updatedImages = reorderedImages.map((img, index) => ({
            ...img,
            order: index + 1,
        }));

        setCurrentImages(updatedImages);
    };

    // Toggle individual image activation
    const toggleImageActive = (imageId: number) => {
        setCurrentImages(
            currentImages.map((img) =>
                img.id === imageId ? { ...img, active: !img.active } : img
            )
        );
    };

    // Handle view image
    const handleViewImage = (imageId: number) => {
        const image = currentImages.find((img) => img.id === imageId);
        if (image) {
            const imgIdx = currentImages
                .filter((i) => i.activityId === image.activityId)
                .findIndex((i) => i.id === image.id);
            const activityDefaults =
                activityBannerImages[image.activityId] || [];
            const url =
                uploadedImages[imageId] ||
                image.url ||
                (image.filename?.startsWith("http") ? image.filename : "") ||
                activityDefaults[imgIdx] ||
                activityDefaults[0] ||
                "";
            if (url) window.open(url, "_blank");
        }
    };

    // Handle delete confirmation
    const handleDeleteClick = (imageId: number) => {
        setImageToDelete(imageId);
        setDeleteConfirmOpen(true);
    };

    const confirmDelete = () => {
        if (imageToDelete !== null) {
            const img = currentImages.find((i) => i.id === imageToDelete);
            // Track server image for deletion
            if (img?.filename) registerDeletedImage(img.filename);

            // Revoke the blob URL if it's an uploaded image to prevent memory leaks
            if (uploadedImages[imageToDelete]) {
                removePendingFile(uploadedImages[imageToDelete]);
                URL.revokeObjectURL(uploadedImages[imageToDelete]);
                const newUploadedImages = { ...uploadedImages };
                delete newUploadedImages[imageToDelete];
                setUploadedImages(newUploadedImages);
            }

            setCurrentImages(
                currentImages.filter((img) => img.id !== imageToDelete)
            );
            setDeleteConfirmOpen(false);
            setImageToDelete(null);
        }
    };

    const cancelDelete = () => {
        setDeleteConfirmOpen(false);
        setImageToDelete(null);
    };

    // Handle image upload
    const handleAddButtonClick = () => {
        fileInputRef.current?.click();
    };

    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        const validTypes = ["image/png", "image/jpeg", "image/jpg"];
        if (!validTypes.includes(file.type)) {
            showErrorToast(
                t("Invalid file format. Please use PNG, JPG or JPEG.")
            );
            return;
        }

        // Validate file size (max 5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        if (file.size > maxSize) {
            showErrorToast(t("File size is too large. Maximum 5MB."));
            return;
        }

        // Create a URL for the uploaded image
        const imageUrl = URL.createObjectURL(file);
        registerPendingFile(imageUrl, file);

        // Get activity label from activity ID
        const activityLabels: Record<string, string> = {
            fastfood: "Fastfood",
            traditional_restaurant: "Traditional restaurant",
            cafe: "Café",
            hotel: "Hotel",
            camping: "Camping",
        };

        // Create new image item — use IDs from both lists to avoid collisions
        const allIds = [...images.Mobile, ...images.Desktop].map(
            (img) => img.id
        );
        const newImageId = Math.max(...allIds, 0) + 1;
        const newImage: ImageItem = {
            id: newImageId,
            order: currentImages.length + 1,
            filename: file.name,
            url: imageUrl,
            type: "image",
            active: true,
            activityId: selectedActivity,
            activityLabel: activityLabels[selectedActivity] || "Fastfood",
        };

        // Add to current display type's images array
        setCurrentImages([...currentImages, newImage]);

        // Store the uploaded image URL
        setUploadedImages({
            ...uploadedImages,
            [newImageId]: imageUrl,
        });

        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    // Reset the banner images back to the default set (no confirmation).
    // Deep-clone so the module-level default is never mutated. The broadcast
    // effect (which depends on `images`) syncs the change to Redux + preview.
    const handleResetImages = (targetKey?: keyof typeof images) => {
        // Clean up the blob URLs we're about to drop, for ALL current uploads —
        // same pairing confirmDelete does per image. Without this: (1) the URLs
        // leak (setUploadedImages({}) makes them unreachable — the unmount
        // cleanup below reads uploadedImagesRef, which will have synced to {},
        // so neither it nor confirmDelete can revoke them later), and (2) the
        // stale files stay in pendingFilesRef and get re-uploaded on save even
        // though the reset removed them.
        Object.values(uploadedImages).forEach((url) => {
            if (url.startsWith("blob:")) {
                removePendingFile(url);
                URL.revokeObjectURL(url);
            }
        });
        const defaults = JSON.parse(JSON.stringify(DEFAULT_BANNER_IMAGES));
        setImages(defaults);
        setUploadedImages({});
        setCurrentSlide(0);
        // Reset cleared uploadedImages, so re-seed the default thumbnails.
        // resetBannerAll forces displayType to "desktop" then calls us in the
        // same tick, so the render-derived `displayKey` is still the OLD value
        // here. Honor an explicit target key (ignoring a non-key arg such as a
        // click event), falling back to the current display type otherwise.
        const seedKey: keyof typeof images =
            targetKey === DISPLAY_KEY_MOBILE ||
            targetKey === DISPLAY_KEY_DESKTOP
                ? targetKey
                : displayKey;
        loadDefaultImageBlobs(defaults[seedKey]);
    };

    // Handle custom text changes
    const handleCustomTitleChange = (value: string) => {
        setCustomTexts({
            ...customTexts,
            [selectedLanguage]: {
                ...customTexts[selectedLanguage],
                title: value,
            },
        });
    };

    const handleCustomDescriptionChange = (value: string) => {
        setCustomTexts({
            ...customTexts,
            [selectedLanguage]: {
                ...customTexts[selectedLanguage],
                description: value,
            },
        });
    };

    // Reset to default translation
    const resetTitle = () => {
        setCustomTexts({
            ...customTexts,
            [selectedLanguage]: {
                ...customTexts[selectedLanguage],
                title: defaultTranslations[selectedLanguage].title,
            },
        });
        setTitleColor("#ffffff");
    };

    const resetDescription = () => {
        setCustomTexts({
            ...customTexts,
            [selectedLanguage]: {
                ...customTexts[selectedLanguage],
                description: defaultTranslations[selectedLanguage].description,
            },
        });
        setDescriptionColor("#ffffff");
    };

    // Reset all (title and description) for current language
    const resetAll = () => {
        setCustomTexts({
            ...customTexts,
            [selectedLanguage]: {
                title: defaultTranslations[selectedLanguage].title,
                description: defaultTranslations[selectedLanguage].description,
            },
        });
        setTitleColor("#ffffff");
        setDescriptionColor("#ffffff");
    };

    // Reset title + description for EVERY language back to defaults.
    const resetAllLanguages = () => {
        setCustomTexts(
            Object.keys(defaultTranslations).reduce((acc, code) => {
                acc[code] = {
                    title: defaultTranslations[code].title,
                    description: defaultTranslations[code].description,
                };
                return acc;
            }, {} as Record<string, { title: string; description: string }>)
        );
        setTitleColor("#ffffff");
        setDescriptionColor("#ffffff");
    };

    // Reset the WHOLE banner rubric to defaults, directly on local state so
    // the UI updates immediately (the broadcast effect then syncs Redux +
    // preview). Done locally rather than via a store-reset + remount, which
    // reactstrap's TabContent does not perform reliably.
    const resetBannerAll = () => {
        setBannerEnabled(true);
        setTitleVisible(true);
        setDescriptionVisible(true);
        setDisplayType("desktop");
        handleResetImages(DISPLAY_KEY_DESKTOP);
        resetAllLanguages();
    };

    // Get all active images with their URLs (from current display type)
    // Priority: 1) blob URL → 2) server URL → 3) filename if http → 4) activity default
    //
    // Memoized: this feeds `previewData`, whose broadcast effect dispatches
    // setBanner. A fresh array every render made previewData unstable, so the
    // effect re-fired → dispatch → re-render → loop ("Maximum update depth
    // exceeded"). Recompute only when its real inputs change.
    const activeImages = React.useMemo(
        () =>
            currentImages
                .filter((img) => img.active)
                .map((img, idx) => {
                    const activityDefaults =
                        activityBannerImages[img.activityId] || [];
                    return {
                        ...img,
                        url:
                            uploadedImages[img.id] ||
                            img.url ||
                            (img.filename?.startsWith("http")
                                ? img.filename
                                : "") ||
                            activityDefaults[idx] ||
                            activityDefaults[0] ||
                            "",
                    };
                }),
        [currentImages, uploadedImages, activityBannerImages]
    );

    // Reset slide index when active images change
    React.useEffect(() => {
        if (currentSlide >= activeImages.length) {
            setCurrentSlide(Math.max(0, activeImages.length - 1));
        }
    }, [activeImages.length, currentSlide]);

    // Seed default-image blobs once on mount (rows without a real URL).
    //
    // Deliberately mount-only (empty deps) — a later re-seed is NOT just
    // redundant, it's harmful: default rows keep `url === ""` after seeding
    // (the blob lives in `uploadedImages`, not on the row), so re-running on a
    // `currentImages` / `displayType` change would re-fetch the same defaults
    // and overwrite `uploadedImages[id]` with a fresh blob URL — leaking the
    // previous one and re-registering a duplicate pending file. The only
    // intentional re-seed is the reset path (handleResetImages), which calls
    // this explicitly after clearing `uploadedImages`. `loadDefaultImageBlobs`
    // is itself stable (useCallback over a module constant + a stable context
    // fn), so no stale closure is captured by omitting it from the deps.
    //
    // Abort any in-flight seed/reset fetches at unmount so their `.then`
    // never creates an orphan blob URL or setState on the unmounted component.
    React.useEffect(() => {
        loadDefaultImageBlobs(currentImages);
        const controller = abortControllerRef.current;
        return () => {
            controller?.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Cleanup blob URLs on component unmount.
    // Track the latest map in a ref so the cleanup effect reads it at unmount
    // without re-running (and revoking still-active URLs) on every change to
    // `uploadedImages`. Mirrors Publicites/index.tsx (currentAdsRef).
    const uploadedImagesRef = React.useRef(uploadedImages);
    React.useEffect(() => {
        uploadedImagesRef.current = uploadedImages;
    }, [uploadedImages]);

    React.useEffect(() => {
        return () => {
            Object.values(uploadedImagesRef.current).forEach((url) => {
                if (url.startsWith("blob:")) {
                    URL.revokeObjectURL(url);
                }
            });
        };
    }, []);

    // Broadcast channel for real-time updates to preview window
    const [
        broadcastChannel,
        setBroadcastChannel,
    ] = React.useState<BroadcastChannel | null>(null);

    // Initialize broadcast channel (for external preview window only)
    React.useEffect(() => {
        const channel = new BroadcastChannel("scan-to-preview-channel");
        setBroadcastChannel(channel);
        return () => {
            channel.close();
        };
    }, []);

    // Single source of truth for the banner preview payload — both the
    // auto-sync effect and the "open in new window" handler ship this exact
    // shape, so they cannot drift (the popup used to receive a truncated
    // subset missing `images` and `displayType`).
    const previewData = React.useMemo(
        () => ({
            mainColor,
            // The preview resolves its text from the shared preview language
            // (not the local form language), so the banner shows the same
            // language as every other section.
            selectedLanguage: previewLanguage,
            bannerEnabled,
            customTexts,
            activeImages: activeImages.map((img) => ({
                id: img.id,
                url: img.url,
                activityId: img.activityId,
                activityLabel: img.activityLabel,
            })),
            titleVisible,
            descriptionVisible,
            titleColor,
            descriptionColor,
            currentSlide,
            // Full config data for Redux persistence
            images,
            displayType,
        }),
        [
            mainColor,
            previewLanguage,
            bannerEnabled,
            customTexts,
            activeImages,
            titleVisible,
            descriptionVisible,
            titleColor,
            descriptionColor,
            currentSlide,
            images,
            displayType,
        ]
    );

    // Share banner preview data via context + BroadcastChannel + sessionStorage
    React.useEffect(() => {
        // Share with other same-page tabs via context
        setSharedBannerData(previewData);

        // Send to external preview window via BroadcastChannel
        if (broadcastChannel) {
            try {
                broadcastChannel.postMessage(previewData);
            } catch (_) {
                /* channel closed */
            }
        }

        // Persist for external window initial load
        sessionStorage.setItem(
            "scanToPreviewData",
            JSON.stringify(previewData)
        );
    }, [previewData, broadcastChannel, setSharedBannerData]);

    return (
        <Container data-testid="scan-to-banner-container">
            <LeftSection data-testid="scan-to-banner-left-section">
                <BannerHeaderGroup>
                    <SectionHeader data-testid="scan-to-banner-header">
                        <SectionTitle data-testid="scan-to-banner-title">
                            {t("Banner")}
                        </SectionTitle>
                        <ToggleContainer data-testid="scan-to-banner-toggle-container">
                            <ToggleLabel>{t("Disable banner")}</ToggleLabel>
                            <Toggle
                                data-testid="scan-to-banner-toggle-enabled"
                                isActive={bannerEnabled}
                                mainColor={mainColor}
                                onClick={() => setBannerEnabled(!bannerEnabled)}
                                aria-label={t("Disable banner")}
                            />
                        </ToggleContainer>
                    </SectionHeader>
                    <ResetAllButton
                        type="button"
                        data-testid="scan-to-banner-reset-btn"
                        mainColor={mainColor}
                        onClick={() => setIsBannerResetOpen(true)}
                        style={{ alignSelf: "flex-end" }}
                    >
                        <ResetIcon style={{ width: ICON_SIZE_SMALL, height: ICON_SIZE_SMALL }} />
                        {t("Reset all")}
                    </ResetAllButton>
                </BannerHeaderGroup>
                {isBannerResetOpen && (
                    <ConfirmationInitializationMessage
                        isModalOpened={isBannerResetOpen}
                        setIsModalOpened={setIsBannerResetOpen}
                        onConfirm={resetBannerAll}
                    />
                )}

                <ImageTable
                    mainColor={mainColor}
                    displayType={displayType}
                    onDisplayTypeChange={setDisplayType}
                    currentImages={currentImages}
                    uploadedImages={uploadedImages}
                    onDragEnd={handleDragEnd}
                    onOrderChange={handleOrderChange}
                    onToggleActive={toggleImageActive}
                    onViewImage={handleViewImage}
                    onDeleteClick={handleDeleteClick}
                    onAddClick={handleAddButtonClick}
                    onResetImages={handleResetImages}
                    fileInputRef={fileInputRef}
                    onImageUpload={handleImageUpload}
                />

                <TitleDescription
                    mainColor={mainColor}
                    selectedLanguage={selectedLanguage}
                    onLanguageChange={setSelectedLanguage}
                    languages={languages}
                    defaultTranslations={defaultTranslations}
                    customTexts={customTexts}
                    onCustomTitleChange={handleCustomTitleChange}
                    onCustomDescriptionChange={handleCustomDescriptionChange}
                    titleVisible={titleVisible}
                    onTitleVisibleChange={() => setTitleVisible(!titleVisible)}
                    descriptionVisible={descriptionVisible}
                    onDescriptionVisibleChange={() =>
                        setDescriptionVisible(!descriptionVisible)
                    }
                    titleColor={titleColor}
                    onTitleColorChange={setTitleColor}
                    descriptionColor={descriptionColor}
                    onDescriptionColorChange={setDescriptionColor}
                    onResetTitle={resetTitle}
                    onResetDescription={resetDescription}
                    onResetAll={resetAll}
                    onResetAllLanguages={resetAllLanguages}
                />
            </LeftSection>

            {/* Delete Confirmation Modal */}
            {deleteConfirmOpen && (
                <DeleteConfirmModal
                    mainColor={mainColor}
                    onCancel={cancelDelete}
                    onConfirm={confirmDelete}
                />
            )}
        </Container>
    );
}
